import React, { useRef } from 'react';
import ReactPlayer from 'react-player';
import Slider from '@mui/material/Slider';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, IconButton, Popover, Stack, Typography,
} from '@mui/material';
import { Circle } from '@mui/icons-material';
import ExpertHubFullWidthWithoutTopbarLayout from 'components/templates/expert-hub-fullwidth-without-tobar.template';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import {
  AngleIcon,
  ArrowIcon,
  BackwardFillIcon,
  CircleOutlineIcon,
  FolderFillIcon,
  ForwardFillIcon,
  LineIcon,
  PauseCircleIcon,
  PencilIcon,
  PlayCircleIcon,
  RectangleOutlineIcon,
  RepeatFillIcon,
  RotateLeftIcon,
  SquareMenuIcon,
  StopCircleIcon,
  VideoSpeedFillIcon,
  VolumeFillIcon,
  VolumeMuteFillIcon,
} from 'assets/icons';
import { IMediaItemData } from 'base/common/media-modal/interface';
import { useParams } from 'react-router-dom';

import { Loader } from 'components';
import ExpertMedia from './components/expert-media';
import UserMedia from './components/user-media';
import UserMessage from './components/user-message';
import YoutubeImport from './components/youtube-import';
import TopBar from './components/top-bar';
import { sliderStyle } from './styles';
import './style.scss';
import { BrushType, Progress } from './interface';
import { format } from './uttils';
import AnnotationArea from './components/annotation-area';
import Tools from './components/tools';
import VideoPlayer from './components/video-player';
import { chatActions } from '../message-hub/slice';

import { selectConversationLoader } from '../message-hub/selectors';

const brushTypes: BrushType[] = [
  { name: 'pencil', icon: <PencilIcon />, title: 'Pencil' },
  { name: 'arrow', icon: <ArrowIcon />, title: 'Arrow' },
  { name: 'line', icon: <LineIcon />, title: 'Line' },
  { name: 'angle', icon: <AngleIcon />, title: 'Angle' },
  { name: 'circle', icon: <CircleOutlineIcon />, title: 'Circle' },
  { name: 'circle-fill', icon: <Circle />, title: 'Circle Fill' },
  { name: 'rectangle', icon: <RectangleOutlineIcon />, title: 'Square' },
  { name: 'rectangle-fill', icon: <SquareMenuIcon />, title: 'Square Fill' },
];
const brushSizes = [1, 3, 6, 12, 24];

interface AnnotationAreaFunctions {
  removeAllHistory(): void;
  removeHistoryItem(): void;
}

const DigitalCoachingTool = () => {
  const dispatch = useDispatch();
  const [isPlaying, setIsPlaying] = React.useState<boolean>(false);
  const playerRef = React.useRef<ReactPlayer | null>(null);
  const [loop, setLoop] = React.useState<boolean>(false);
  const [volume, setVolume] = React.useState<number>(0.5);
  const [speed, setSpeed] = React.useState<number>(1);
  const [marks, setMarks] = React.useState<{ value: number }[]>([]);
  const [url, setUrl] = React.useState<string>('');
  const [isVideo, setIsVideo] = React.useState<boolean>(false);
  const [selectedBrushType, setSelectedBrushType] = React.useState<BrushType>(brushTypes[0]);
  const [brushSize, setBrushSize] = React.useState<number>(3);
  const [highlight, setHighlight] = React.useState<boolean>(false);
  const [brushColors, setBrushColors] = React.useState<string[]>(['#B5E69A', '#F49E9E', '#3DCC96', '#FF3A3A', '#000000', '#0606A5']);
  const [brushColor, setBrushColor] = React.useState<string>('#0606A5');
  const annotationAreaRef = React.useRef<AnnotationAreaFunctions>(null);
  const [volumeAnchorEl, setVolumeAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [speedAnchorEl, setSpeedAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [showSideBar, setShowSideBar] = React.useState<boolean>(true);
  const isVolumePopperOpen = Boolean(volumeAnchorEl);
  const isSpeedPopperOpen = Boolean(speedAnchorEl);
  const initialProgress = {
    played: 0, playedSeconds: 0, loaded: 0, loadedSeconds: 0,
  };

  const { conversationId } = useParams();
  const conversationLoading = useSelector(selectConversationLoader);

  const fullscreenContainerRef = useRef(null);

  // handle fullscreen
  const fullscreenHandler = useFullScreenHandle();
  const { active: fullscreen, enter, exit } = fullscreenHandler;

  const [progress, setProgress] = React.useState<Progress>(initialProgress);

  const handleChange = (event: Event, newValue: number | number[]) => {
    const val = (newValue as number / 100);
    playerRef.current?.seekTo(parseFloat(String(val)), 'fraction');
  };

  const play = () => {
    setIsPlaying(true);
  };

  const pause = () => {
    setIsPlaying(false);
  };

  const stop = () => {
    setIsPlaying(false);
    playerRef.current?.seekTo(0);
  };

  const ProgressHandler = (progressData: Progress) => {
    // TODO: setProgress based seeking;
    setProgress(progressData);
  };

  const forward = () => {
    const currentTime = playerRef.current?.getCurrentTime() || 0;
    playerRef.current?.seekTo(currentTime + 5);
  };
  const backward = () => {
    const currentTime = playerRef.current?.getCurrentTime() || 0;
    playerRef.current?.seekTo(currentTime - 5);
  };

  const toggleLoop = () => {
    setLoop(!loop);
  };

  const handleYoutubeImport = (videoUrl: string) => {
    stop();
    setMarks([]);
    setProgress(initialProgress);
    setIsVideo(true);
    setUrl(videoUrl);
  };

  const handleMediaSelect = React.useCallback((media: IMediaItemData) => {
    stop();
    setMarks([]);
    setProgress(initialProgress);
    setIsVideo(media.fileCategory === 'video');
    setUrl(media.contentUrl);
  }, []);

  React.useEffect(() => {
    annotationAreaRef?.current?.removeAllHistory();
  }, [url]);

  React.useEffect(() => {
    if (conversationId) {
      dispatch(chatActions.setConversationId(conversationId));
      dispatch(chatActions.getSingleConversation());
    }
  }, [dispatch, conversationId]);

  const openVolumePopper = (event: any) => {
    setVolumeAnchorEl(event.currentTarget);
  };

  const closeVolumePopper = () => {
    setVolumeAnchorEl(null);
  };

  const openSpeedPopper = (event: any) => {
    setSpeedAnchorEl(event.currentTarget);
  };

  const closeSpeedPopper = () => {
    setSpeedAnchorEl(null);
  };

  const addMarkToTimeline = () => {
    setMarks((preMarks) => [...preMarks, { value: progress.played * 100 }]);
  };

  const usermessage = React.useMemo(
    () => <UserMessage />,
    [],
  );

  const usermedia = React.useMemo(
    () => <UserMedia onMediaSelect={handleMediaSelect} />,
    [handleMediaSelect],
  );

  const expertmedia = React.useMemo(
    () => <ExpertMedia onMediaSelect={handleMediaSelect} />,
    [handleMediaSelect],
  );

  return (
    <ExpertHubFullWidthWithoutTopbarLayout>
      <Loader loading={conversationLoading}>
        <Popover
          open={isVolumePopperOpen}
          anchorEl={volumeAnchorEl}
          onClose={closeVolumePopper}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          container={fullscreenContainerRef.current}
        >
          <div
            className="p-0"
            style={{
              backgroundColor: 'background.paper', border: '1px solid #E7E7F1', borderRadius: '6px', overflow: 'hidden',
            }}
          >
            <Stack sx={{ height: 70, my: 1 }} spacing={1} direction="column" alignItems="center">
              <Slider
                size="small"
                aria-label="Volume"
                orientation="vertical"
                valueLabelDisplay="off"
                value={volume * 100}
                onChange={(event: Event, newValue: number | number[]) => {
                  if (typeof newValue === 'number') {
                    setVolume(newValue / 100);
                  }
                }}
                sx={{ mb: 0.5 }}
              />
              <IconButton
                sx={{ p: 0 }}
                onClick={() => {
                  if (volume === 0) {
                    setVolume(0.5);
                  } else {
                    setVolume(0);
                  }
                }}
              >
                {volume === 0 ? (
                  <VolumeMuteFillIcon className="cursor-pointer" sx={{ color: 'primary.main', fontSize: 15 }} />
                ) : (
                  <VolumeFillIcon className="cursor-pointer" sx={{ color: 'primary.main', fontSize: 15 }} />
                )}
              </IconButton>
            </Stack>
          </div>
        </Popover>
        <Popover
          open={isSpeedPopperOpen}
          anchorEl={speedAnchorEl}
          onClose={closeSpeedPopper}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          container={fullscreenContainerRef.current}
        >
          <div
            className="p-0"
            style={{
              backgroundColor: 'background.paper', border: '1px solid #E7E7F1', borderRadius: '6px', overflow: 'hidden',
            }}
          >
            <Stack sx={{ height: 70, my: 1 }} spacing={1} direction="column" alignItems="center">
              <Slider
                size="small"
                aria-label="Volume"
                orientation="vertical"
                valueLabelDisplay="off"
                value={speed}
                onChange={(event: Event, newValue: number | number[]) => {
                  if (typeof newValue === 'number') {
                    setSpeed(newValue);
                  }
                }}
                step={0.1}
                min={0}
                max={3}
                sx={{ mb: 0.5 }}
              />
              <IconButton
                sx={{ p: 0 }}
                onClick={() => {
                  setSpeed(1);
                }}
              >
                <RotateLeftIcon className="cursor-pointer" sx={{ color: 'primary.dark', fontSize: 15 }} />
              </IconButton>
            </Stack>
          </div>
        </Popover>
        <FullScreen handle={fullscreenHandler} className={`row coaching-tool-main-wrap ${fullscreen ? 'fullscreen-enabled' : ''}`}>
          {showSideBar && (
            <Box
              className="coaching-tool-sidebar col-12 col-lg-3 col-xl-3 col-xxl-2"
              sx={{
                border: 1,
                borderColor: 'divider',
                p: 3,
                pb: 0,
                backgroundColor: 'background.paper',
              }}
            >
              {conversationId && (
                <>
                  {usermessage}
                  {usermedia}
                </>
              )}
              <YoutubeImport importHandler={handleYoutubeImport} />
              {expertmedia}
            </Box>
          )}

          <div className={`col-12 ${showSideBar ? 'col-lg-9 col-xl-9 col-xxl-10' : 'col-lg-12 col-xl-12 col-xxl-12'} d-flex flex-column position-relative`} ref={fullscreenContainerRef}>
            <TopBar
              enterFullScreen={enter}
              exitFullScreen={exit}
              fullscreen={fullscreen}
              showSideBar={showSideBar}
              container={fullscreenContainerRef.current}
              toggleSideBar={(isDisplay: boolean) => setShowSideBar(isDisplay)}
            />
            <div className="video-content-container flex-fill mx-n2">
              <div className="d-flex flex-row align-items-center h-100 w-100 overflow-hidden">
                <div className="flex-grow-1 h-100 d-flex flex-column">
                  <div className={`video-wrapper ${!isVideo ? 'full-height-wrapper' : ''} d-flex align-items-center justify-content-center pt-3 pt-md-4 px-3 px-lg-4 px-xl-4 position-relative`}>
                    {url && url !== '' ? (
                      <>
                        {isVideo ? (
                          <VideoPlayer
                            isPlaying={isPlaying}
                            playerRef={playerRef}
                            progressHandler={ProgressHandler}
                            handleIsPlaying={setIsPlaying}
                            loop={loop}
                            url={url}
                            volume={volume}
                            playbackRate={speed}
                          />
                        ) : (
                          <div className="image-player">
                            <img src={url} alt="test" />
                          </div>
                        )}
                        <AnnotationArea
                          ref={annotationAreaRef}
                          brushTypes={brushTypes}
                          selectedBrushType={selectedBrushType}
                          onBrushSelect={(brushType) => { setSelectedBrushType(brushType); }}
                          size={brushSize}
                          highlight={highlight}
                          color={brushColor}
                          fullscreen={fullscreen}
                          showSideBar={showSideBar}
                        />
                      </>
                    ) : (
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>Select a video or image</Typography>
                    )}
                  </div>
                  {url && url !== '' && isVideo ? (
                    <div className="video-slider px-3 px-lg-4 px-xl-5 mb-3">
                      <Box sx={{}}>
                        <Slider
                          aria-label="Volume"
                          value={(progress.played * 100)}
                          onChange={handleChange}
                          sx={sliderStyle}
                          min={0}
                          max={100}
                          marks={marks}
                        />
                      </Box>
                      <Box
                        sx={{
                          py: 1,
                          px: 2,
                          boxShadow: '0px 8px 60px #00000005',
                          borderRadius: 1.5,
                          backgroundColor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                        }}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <Stack alignItems="center" direction="row" spacing={1}>

                          {isPlaying ? (
                            <IconButton sx={{ p: 0 }} onClick={pause}>
                              <PauseCircleIcon className="cursor-pointer" sx={{ color: 'primary.dark' }} />
                            </IconButton>
                          ) : (
                            <IconButton sx={{ p: 0 }} onClick={play}>
                              <PlayCircleIcon className="cursor-pointer" sx={{ color: 'primary.dark' }} />
                            </IconButton>
                          )}
                          <IconButton sx={{ p: 0 }} onClick={stop}>
                            <StopCircleIcon className="cursor-pointer" sx={{ color: 'primary.dark' }} />
                          </IconButton>
                          <IconButton sx={{ p: 0 }} onClick={backward}>
                            <BackwardFillIcon className="cursor-pointer" sx={{ color: 'primary.dark' }} />
                          </IconButton>
                          <IconButton sx={{ p: 0 }} onClick={forward}>
                            <ForwardFillIcon className="cursor-pointer" sx={{ color: 'primary.dark' }} />
                          </IconButton>
                          <Typography variant="body2" sx={{ color: 'primary.dark', pl: 0.75 }}>
                            {/* {`${plyedTime.toFixed(2)} / 12:25`} */}
                            {`${format(progress.playedSeconds).toString()} / ${format(progress.loadedSeconds).toString()}`}
                          </Typography>
                        </Stack>
                        <Stack alignItems="center" direction="row" spacing={1.25}>
                          <div className="d-flex align-items-center">
                            <IconButton
                              sx={{ p: 0, mr: 0.5 }}
                              onClick={openSpeedPopper}
                            >
                              <VideoSpeedFillIcon className="cursor-pointer" sx={{ color: 'primary.dark' }} />
                            </IconButton>
                            <Typography variant="body2" sx={{ color: 'primary.dark', minWidth: 27 }}>
                              {`${speed}x`}
                            </Typography>
                          </div>
                          <IconButton sx={{ p: 0 }} onClick={toggleLoop}>
                            <RepeatFillIcon className="cursor-pointer" sx={{ color: !loop ? 'primary.dark' : 'primary.main' }} />
                          </IconButton>
                          <IconButton sx={{ p: 0 }} onClick={addMarkToTimeline}>
                            <FolderFillIcon className="cursor-pointer" sx={{ color: 'primary.dark' }} />
                          </IconButton>
                          <IconButton
                            sx={{ p: 0 }}
                            onClick={openVolumePopper}
                          >
                            {volume === 0 ? (
                              <VolumeMuteFillIcon className="cursor-pointer" sx={{ color: 'primary.main' }} />
                            ) : (
                              <VolumeFillIcon className="cursor-pointer" sx={{ color: 'primary.dark' }} />
                            )}
                          </IconButton>
                        </Stack>

                      </Box>
                    </div>
                  ) : (null)}
                </div>
                {url && url !== '' ? (
                  <div className="tools-wrapper">
                    <Tools
                      brushTypes={brushTypes}
                      selectedBrushType={selectedBrushType}
                      onBrushSelect={(brushType) => { setSelectedBrushType(brushType); }}
                      brushSizes={brushSizes}
                      selectedBrushSize={brushSize}
                      onBrushSizeChange={(size: number) => { setBrushSize(size); }}
                      highlight={highlight}
                      onToggleHighlight={(value: boolean) => { setHighlight(value); }}
                      brushColors={brushColors}
                      setBrushColors={
                        (color: string) => {
                          setBrushColors((prevColors) => [...prevColors, color]);
                        }
                      }
                      selectedBrushColor={brushColor}
                      onColorSelect={(color: string) => { setBrushColor(color); }}
                      onHistoryAllClear={() => {
                        annotationAreaRef.current?.removeAllHistory();
                      }}
                      onHistoryRedo={() => {
                        annotationAreaRef.current?.removeHistoryItem();
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </FullScreen>
      </Loader>
    </ExpertHubFullWidthWithoutTopbarLayout>
  );
};

export default DigitalCoachingTool;
